
import { db, auth } from "../firebase";
import { getRoleByEmail, getDeptCodeByEmail, isSuperUser } from "../config/roles";
import { getDeptNameByCode } from "../constants/departments";
import { canViewData } from "../utils/dataAccess";

// Helper to construct AppUser object from Firebase Auth user
function getCurrentAppUser() {
    const u = auth.currentUser;
    if (!u) return null;
    return {
        id: u.uid,
        email: u.email || "",
        name: u.displayName || "",
        role: getRoleByEmail(u.email),
        displayName: u.displayName || ""
    };
}

export interface DashboardStats {
  contracts: {
    active: number;
    expiringIn90Days: number;
    renewedThisYear: number;
    highRiskAmount: number;
  };
  cases: {
    ongoing: number;
    closed: number;
    nearDeadline30Days: number;
    highAmount: number;
  };
  compliance: {
    thisMonthCount: number;
  };
  reviews: {
    pendingContracts: number;
    pendingSealRequests: number;
    pendingGovLetters: number;
  };
  notifications: {
    unreadCount: number;
  };
  // New specific buckets for internal dashboard
  contractAlerts: {
    expiringIn60Days: number;
    expiringIn30Days: number;
    expiringToday: number;
    expired: number;
  };
  reviewAlerts: {
    overdue: number;
    dueToday: number;
    dueIn7Days: number;
  };
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const user = getCurrentAppUser();
  // Initialize with zeros
  const emptyStats = { 
      contracts: { active: 0, expiringIn90Days: 0, renewedThisYear: 0, highRiskAmount: 0 }, 
      cases: { ongoing: 0, closed: 0, nearDeadline30Days: 0, highAmount: 0 }, 
      compliance: { thisMonthCount: 0 }, 
      reviews: { pendingContracts: 0, pendingSealRequests: 0, pendingGovLetters: 0 }, 
      notifications: { unreadCount: 0 },
      contractAlerts: { expiringIn60Days: 0, expiringIn30Days: 0, expiringToday: 0, expired: 0 },
      reviewAlerts: { overdue: 0, dueToday: 0, dueIn7Days: 0 }
  };

  if (!user) return emptyStats;

  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const date90 = new Date(); date90.setDate(date90.getDate() + 90);
  const str90 = date90.toISOString().split('T')[0];
  const yearStart = `${now.getFullYear()}-01-01`;

  // 1. Contracts (from legal_contracts)
  const snapC = await db.collection("legal_contracts").get();
  const contractsRaw = snapC.docs.map(d => d.data() as any);
  const contracts = contractsRaw.filter(c => canViewData(user, c));
  
  // 2. Cases
  const snapCase = await db.collection("legal_matters").get();
  const casesRaw = snapCase.docs.map(d => d.data() as any);
  const cases = casesRaw.filter(c => canViewData(user, c));

  // 3. Approval Requests (Unified Collection)
  const snapReq = await db.collection("approval_requests").get();
  const reqsRaw = snapReq.docs.map(d => d.data() as any);
  const reqs = reqsRaw.filter(r => canViewData(user, r));
  
  const pendingStatuses = ['SUBMITTED', 'IN_REVIEW', 'WAITING_APPROVAL'];

  // 4. Compliance (Real Data from Firestore)
  const snapCompliance = await db.collection("legal_compliance_projects").get();
  const complianceProjectsRaw = snapCompliance.docs.map(d => d.data() as any);
  const complianceProjects = complianceProjectsRaw.filter(p => canViewData(user, p));
  
  const currentMonthStr = todayStr.slice(0, 7); // YYYY-MM
  const complianceCount = complianceProjects.filter(p => 
    (p.nextDeadline && p.nextDeadline.startsWith(currentMonthStr)) || 
    (p.frequency && (p.frequency.includes('月') || p.frequency.includes('逐批')))
  ).length;

  // 5. Notifications
  const snapNotif = await db.collection("notifications").where("read", "==", false).get();

  // --- New Logic: Contract Alerts ---
  // Rules: Active contracts only. D = endDate - today.
  // Buckets: 60, 30, 0, <0(expired)
  const contractAlerts = { expiringIn60Days: 0, expiringIn30Days: 0, expiringToday: 0, expired: 0 };
  
  contracts.forEach(c => {
      const isActive = c.status === "生效中" || c.status === "ACTIVE" || c.status === "IN_FORCE";
      // Check if not deleted/terminated logic if fields exist, but status check usually covers it
      if (isActive && c.periodEnd) {
          const diff = Math.floor((new Date(c.periodEnd).getTime() - new Date(todayStr).getTime()) / 86400000);
          
          if (diff < 0) contractAlerts.expired++;
          else if (diff === 0) contractAlerts.expiringToday++;
          else if (diff === 30) contractAlerts.expiringIn30Days++;
          else if (diff === 60) contractAlerts.expiringIn60Days++;
      }
  });

  // --- New Logic: Review Alerts ---
  // Rules: Pending Reviews. R = deadline - today.
  // Buckets: <0 (overdue), 0 (today), 1-7 (7days)
  const reviewAlerts = { overdue: 0, dueToday: 0, dueIn7Days: 0 };
  
  reqs.filter(r => pendingStatuses.includes(r.status)).forEach(r => {
      // Determine deadline. Try 'dueDate', 'expectedDueDate', or calculate from 'submittedAt' + 4 days
      let deadline = r.dueDate || r.expectedDueDate;
      if (!deadline && r.submittedAt) {
          const subDate = r.submittedAt.toDate ? r.submittedAt.toDate() : new Date(r.submittedAt);
          // Simple +5 days logic for fallback
          const d = new Date(subDate);
          d.setDate(d.getDate() + 5);
          deadline = d.toISOString().split('T')[0];
      }

      if (deadline) {
          const diff = Math.floor((new Date(deadline).getTime() - new Date(todayStr).getTime()) / 86400000);
          if (diff < 0) reviewAlerts.overdue++;
          else if (diff === 0) reviewAlerts.dueToday++;
          else if (diff >= 1 && diff <= 7) reviewAlerts.dueIn7Days++;
      }
  });

  return {
    contracts: {
      active: contracts.filter(c => c.status === "生效中").length,
      expiringIn90Days: contracts.filter(c => c.periodEnd && c.periodEnd >= todayStr && c.periodEnd <= str90).length,
      renewedThisYear: contracts.filter(c => (c.signDate && c.signDate >= yearStart) || (c.createdAt && c.createdAt >= yearStart)).length,
      highRiskAmount: contracts.filter(c => (c.totalAmount || 0) >= 1000000).length
    },
    cases: {
      ongoing: cases.filter(c => c.status !== "已結案").length,
      closed: cases.filter(c => c.status === "已結案").length,
      nearDeadline30Days: 0, 
      highAmount: cases.filter(c => (c.disputeAmount || 0) >= 1000000).length
    },
    reviews: {
      pendingContracts: reqs.filter(r => r.module === 'contractDraft' && pendingStatuses.includes(r.status)).length,
      pendingSealRequests: reqs.filter(r => r.module === 'sealRequest' && pendingStatuses.includes(r.status)).length,
      pendingGovLetters: reqs.filter(r => r.module === 'govLetter' && pendingStatuses.includes(r.status)).length
    },
    compliance: {
      thisMonthCount: complianceCount
    },
    notifications: {
      unreadCount: snapNotif.size
    },
    contractAlerts,
    reviewAlerts
  };
}

// Internal Logic: Build Summary String based on Stats and Rules
export function buildInternalDashboardSummary(
  stats: DashboardStats,
  role: "kevin" | "ops" | "gm"
): string {
  const parts: string[] = [];

  // 1. General Workload
  // Note: Stats are currently global/filtered by view. 
  // We customize the "narrative" based on the role assumption.
  
  if (role === "kevin") {
      parts.push(`今天你有 ${stats.reviews.pendingContracts} 件合約草案、${stats.reviews.pendingSealRequests} 件用印申請待審。`);
  } else {
      // Managers (Ops/Gm) see "Waiting Approval" usually, but here we use total pending as a proxy for "Team Workload"
      const totalPending = stats.reviews.pendingContracts + stats.reviews.pendingSealRequests + stats.reviews.pendingGovLetters;
      parts.push(`今天系統共有 ${totalPending} 件待簽核事項等待處理。`);
  }

  // 2. Date Alerts (Shared logic)
  const alerts: string[] = [];
  
  // Contract Alerts
  if (stats.contractAlerts.expiringToday > 0) alerts.push(`${stats.contractAlerts.expiringToday} 件合約今天到期`);
  if (stats.contractAlerts.expiringIn30Days > 0) alerts.push(`${stats.contractAlerts.expiringIn30Days} 件合約 30 天後到期`);
  if (stats.contractAlerts.expiringIn60Days > 0) alerts.push(`${stats.contractAlerts.expiringIn60Days} 件合約 60 天後到期`);
  
  // Review Alerts
  if (stats.reviewAlerts.dueToday > 0) alerts.push(`${stats.reviewAlerts.dueToday} 件會審今天截止`);
  if (stats.reviewAlerts.overdue > 0) alerts.push(`${stats.reviewAlerts.overdue} 件會審已逾期`);

  if (alerts.length > 0) {
      parts.push(`重要提醒：${alerts.join("、")}。`);
  } else {
      parts.push("目前無緊急到期或逾期事項。");
  }

  // 3. Encouragement
  if (stats.reviewAlerts.overdue > 0 || stats.contractAlerts.expired > 0) {
      parts.push("有需要可以優先處理最急的案件。💪");
  } else {
      parts.push("進度掌控良好，請保持節奏。👍");
  }

  return parts.join("\n");
}

// Send Email via Backend (Internal Only)
export async function sendDashboardSummaryToBackend(summaries: { kevin: string, ops: string, gm: string }) {
    const backendUrl = "https://asia-east1-kevin-erp-demo.cloudfunctions.net/sendDashboardSummaryMail";
    
    const res = await fetch(backendUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ summaries })
    });

    if (!res.ok) {
        throw new Error(`Email sending failed: ${res.statusText}`);
    }
    return await res.json();
}
